"use client";

import { useEffect, useMemo, useState } from "react";
import { FiEdit3 } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { RiDeleteBinLine } from "react-icons/ri";
import { TbNotebook } from "react-icons/tb";
import noteAPI from "../services/note/noteAPI";
import ConfirmPopUp from "./popup/confirmPopUp";
import StatusPopUp from "./popup/statusPopUp";
import { formatCompactDateTime, formatCompactDate } from "../utils/formatDatetime";

export type InformationDetailNote = {
    id: number | string;
    name: string;
    detail: string;
    create_at: string;
};

export type InformationDetailData = {
    uniqueId: string;
    source: string;
    subject: string;
    exerciseName: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    description: string;
    notes: InformationDetailNote[];
};

type InformationDetailProps = {
    isOpen: boolean;
    detail: InformationDetailData | null;
    onClose: () => void;
    onCreateNote: (detail: InformationDetailData) => void;
    onEditNote: (detail: InformationDetailData, note: InformationDetailNote) => void;
    onDeleteNoteSuccess?: (detailUniqueId: string, noteId: number | string) => void;
};

function DetailLine({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E]">
            <div className="grid grid-cols-[140px_1fr] items-start gap-2 text-lg">
                <span>{label}</span>
                <span className="wrap-break-word">{value || "-"}</span>
            </div>
        </div>
    );
}

export default function InformationDetail({
    isOpen,
    detail,
    onClose,
    onCreateNote,
    onEditNote,
    onDeleteNoteSuccess,
}: InformationDetailProps) {
    const [deleteTargetNote, setDeleteTargetNote] = useState<InformationDetailNote | null>(null);
    const [isDeletingNote, setIsDeletingNote] = useState(false);
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);

    const noteRows = useMemo(() => detail?.notes ?? [], [detail?.notes]);
    const sourceLabel = String(detail?.source ?? "").toLowerCase() === "sequence" ? "ชุดแบบฝึกหัด" : "ชื่อ";

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const prevHtmlOverflow = document.documentElement.style.overflow;
        const prevBodyOverflow = document.body.style.overflow;

        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";

        return () => {
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.body.style.overflow = prevBodyOverflow;
        };
    }, [isOpen]);

    const handleDeleteNote = async () => {
        if (!detail || !deleteTargetNote) {
            return;
        }

        setIsDeletingNote(true);
        const response = await noteAPI.softDelete(deleteTargetNote.id);
        setIsDeletingNote(false);

        if (!response || (response as { status?: string }).status === "failed") {
            setStatusPopUp({
                title: "ลบไม่สำเร็จ",
                message: "ไม่สามารถลบโน้ตได้ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            return;
        }

        onDeleteNoteSuccess?.(detail.uniqueId, deleteTargetNote.id);
        setDeleteTargetNote(null);
        setStatusPopUp({
            title: "สำเร็จ",
            message: "ลบโน้ตเรียบร้อยแล้ว",
            type: "success",
        });
    };

    if (!isOpen || !detail) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-4">
                <div className="w-full max-w-[624px] rounded-md bg-[#F4F4F4] px-4 pb-5 pt-4 shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold leading-none text-[#4E4E4E]">{detail.subject || "-"}</h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#d5d9dd] text-[#8e949b] hover:bg-[#d5d9dd]/90"
                        >
                            <IoClose size={20} />
                        </button>
                    </div>

                    <div className="mt-3 space-y-2 text-lg">
                        <DetailLine label="วันที่สร้าง" value={formatCompactDateTime(detail.createdAt)} />
                        <DetailLine label="อัพเดทล่าสุด" value={formatCompactDateTime(detail.updatedAt)} />
                        <DetailLine label="สร้างโดย" value={detail.createdBy || "-"} />
                        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E]">
                            <div className="grid grid-cols-[140px_1fr_auto] items-start gap-2 text-lg">
                                <span>{sourceLabel}</span>
                                <span className="wrap-break-word">{detail.exerciseName || "-"}</span>
                                <span />
                            </div>
                        </div>
                        <div className="rounded-sm bg-white px-3 py-2 text-[#4E4E4E]">
                            <div className="grid grid-cols-[140px_1fr] items-start gap-2 text-lg">
                                <span>คำบรรยายกิจกรรม</span>
                                <span className="wrap-break-word whitespace-pre-line">{detail.description || "-"}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-3 rounded-sm bg-white px-3 py-2">
                        <div className="flex items-center justify-between text-[#4E4E4E]">
                            <span className="text-lg">บันทึก (noted)</span>
                            <button
                                type="button"
                                onClick={() => onCreateNote(detail)}
                                className="inline-flex cursor-pointer items-center gap-1.5 text-[26px] text-[#06A7E4] hover:text-[#008cc3]"
                            >
                                <TbNotebook />
                                <span className="text-base font-medium">เพิ่มโน้ต</span>
                            </button>
                        </div>

                        {noteRows.length === 0 ? (
                            <div className="py-4 text-center text-lg text-[#9b9b9b]">ยังไม่มีโน้ต</div>
                        ) : (
                            <div className="mt-1 space-y-1.5">
                                {noteRows.map((note) => (
                                    <div key={String(note.id)} className="grid grid-cols-[90px_1fr_auto] items-center gap-2 text-[#4E4E4E] p-1">
                                        <span className="text-lg leading-none">{formatCompactDate(note.create_at)}</span>
                                        <div>
                                            <div className="text-lg leading-none">{note.name || "-"}</div>
                                            {/* <div className="mt-1 text-lg leading-snug text-[#6a6a6a]">{note.detail || "-"}</div> */}
                                        </div>
                                        <div className="flex items-center gap-3 pr-1 text-[#06A7E4]">
                                            <button
                                                type="button"
                                                onClick={() => onEditNote(detail, note)}
                                                className="cursor-pointer hover:text-[#008cc3]"
                                                title="Edit note"
                                            >
                                                <FiEdit3 size={22} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDeleteTargetNote(note)}
                                                className="cursor-pointer text-[#f35a5a] hover:text-[#d43f3f]"
                                                title="Delete note"
                                            >
                                                <RiDeleteBinLine size={22} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {deleteTargetNote ? (
                <ConfirmPopUp
                    title="ยืนยันการลบ"
                    message="ต้องการลบโน้ตนี้หรือไม่"
                    confirmText="ตกลง"
                    cancelText="ยกเลิก"
                    onConfirm={() => {
                        void handleDeleteNote();
                    }}
                    onCancel={() => {
                        if (!isDeletingNote) {
                            setDeleteTargetNote(null);
                        }
                    }}
                    isConfirming={isDeletingNote}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}
        </>
    );
}
